import React, { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";
import { ContractTemplate, TemplatePayload, NET_TERMS_OPTIONS, NetTerms } from "./contractTypes";
import { renderMarkdown } from "./markdownUtils";
import LoadingSpinner from "@/components/LoadingSpinner";

// ── TemplateEditor modal ─────────────────────────────────────────────────

interface TemplateEditorProps {
  template?: ContractTemplate;
  onSave: () => void;
  onClose: () => void;
}

function TemplateEditor({ template, onSave, onClose }: TemplateEditorProps) {
  const api = useApi();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TemplatePayload>({
    name: template?.name ?? "",
    body: template?.body ?? "",
    netTerms: template?.netTerms ?? "Net 30",
  });
  const [showPreview, setShowPreview] = useState(false);

  async function handleSave() {
    if (!form.name.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      let res: Response;
      if (template) {
        res = await api.messaging.put(`Contract/templates/${template.id}`, form);
      } else {
        res = await api.messaging.post("Contract/templates", form);
      }
      if (res.ok) {
        onSave();
        onClose();
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {template ? "Edit Template" : "New Template"}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
                placeholder="e.g., Standard MSA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Default Net Terms
              </label>
              <select
                value={form.netTerms}
                onChange={(e) => setForm((f) => ({ ...f, netTerms: e.target.value as NetTerms }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
              >
                {NET_TERMS_OPTIONS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">
                Contract Body (Markdown)
              </label>
              <button
                onClick={() => setShowPreview((p) => !p)}
                className="text-xs text-cornflower-blue-600 hover:underline"
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-2">
              Available placeholders:{" "}
              {["{{customer_name}}", "{{customer_company}}", "{{start_date}}", "{{end_date}}", "{{monthly_value}}", "{{net_terms}}"].map((p) => (
                <code key={p} className="bg-slate-100 px-1 rounded mr-1">{p}</code>
              ))}
            </p>
            <div className={"grid gap-4 " + (showPreview ? "grid-cols-2" : "grid-cols-1")}>
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                rows={16}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 resize-none"
                placeholder="Enter contract template in markdown..."
              />
              {showPreview && (
                <div
                  className="border border-slate-200 rounded-lg p-4 overflow-y-auto text-sm text-slate-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(form.body) }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.body.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-cornflower-blue-500 rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ContractTemplates page ────────────────────────────────────────────────

export default function ContractTemplates() {
  const api = useApi();
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<ContractTemplate | null | "new">(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  async function load() {
    try {
      const res = await api.messaging.get("Contract/templates");
      if (res.ok) {
        const data = await res.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id: number) {
    if (!window.confirm("Delete this template?")) return;
    setDeleting(id);
    try {
      await api.messaging.delete(`Contract/templates/${id}`);
      await load();
    } catch {
      // silent
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">Contract Templates</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Reusable markdown templates with placeholder substitution
          </p>
        </div>
        <button
          onClick={() => setEditTarget("new")}
          className="flex items-center gap-2 px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600"
        >
          + New Template
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-4xl mb-3">&#128196;</div>
            <p className="font-medium">No templates yet</p>
            <p className="text-sm mt-1">Create your first reusable contract template</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Net Terms</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Preview</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                    <td className="px-4 py-3 text-slate-600">{t.netTerms}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-xs truncate">
                      {t.body.slice(0, 100)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditTarget(t)}
                          className="text-cornflower-blue-600 hover:underline text-xs font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(t.id)}
                          disabled={deleting === t.id}
                          className="text-red-500 hover:underline text-xs font-medium"
                        >
                          {deleting === t.id ? "..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editTarget !== null && (
        <TemplateEditor
          template={editTarget === "new" ? undefined : editTarget}
          onSave={load}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
